import { Box } from "../../components/ui/box";
import { BOX_GROUP } from "../../components/ui/box-classes";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../components/ui/collapsible";
import { Icon } from "../../components/ui/icon";
import {
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
} from "../../components/ui/sidebar";
import type { IconName } from "../../components/ui/icon-registry";
import type React from "react";
import type { Omittable } from "../../lib/props";

export type SidebarSectionProps = Omittable<{
  /** Group heading; doubles as the collapse trigger. */
  title: string;
  defaultOpen?: boolean;
  /**
   * Optional header affordance (e.g. a "+" create action). Rendered as the
   * group's own action button — a sibling of the heading, never nested inside
   * the collapse trigger — and revealed on hover.
   */
  action?: { icon: IconName; label: string; onClick: () => void };
  /** `SidebarMenuItem` rows. The section supplies the enclosing menu list. */
  children: React.ReactNode;
}>;

/**
 * A collapsible titled group of sidebar rows: the base `SidebarGroup` parts
 * driven by a `Collapsible`, with the heading as the trigger.
 *
 * Domain-free composition — it knows nothing about SalesTrak — so it lives in
 * the app's particle tier (the promotion queue to `packages/ui`) rather than
 * beside the navigation atoms that consume it.
 */
export function SidebarSection({
  title,
  defaultOpen = true,
  action,
  children,
}: SidebarSectionProps) {
  return (
    <Collapsible
      defaultOpen={defaultOpen}
      // The reveal scope for the header action below. Taken from the one
      // `BOX_GROUP` list so the pair cannot drift into a silent no-op.
      render={<SidebarGroup className={BOX_GROUP.section} />}
    >
      <SidebarGroupLabel
        // Chevron rotation mirrors the accordion trigger's idiom:
        // the trigger carries `data-panel-open`, the indicator is its child.
        className="cursor-pointer data-panel-open:*:data-[slot=icon]:rotate-180"
        render={<CollapsibleTrigger />}
      >
        {title}
        {/* The chevron trails the title. The row's right edge belongs to
            `SidebarGroupAction`, which is absolutely positioned there. */}
        <Icon className="ms-1 transition-transform" name="chevron-down" size="sm" />
      </SidebarGroupLabel>
      {action != null && (
        // Hover-revealed on pointer devices, always visible on touch (where
        // hover never fires).
        <Box
          hoverReveal="section"
          revealOnTouch
          render={
            <SidebarGroupAction
              aria-label={action.label}
              onClick={action.onClick}
            />
          }
        >
          <Icon name={action.icon} size="sm" />
        </Box>
      )}
      <CollapsibleContent render={<SidebarGroupContent />}>
        <SidebarMenu>{children}</SidebarMenu>
      </CollapsibleContent>
    </Collapsible>
  );
}
